/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useCallback, useEffect } from 'react';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { useService } from '@jira/platform__use-service/src/useService.tsx';
import { useOrgId } from '@jira/router-resources__navigation-organization-id/src/useOrgId.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';

const getUserOrgPermissions = async ({ orgId, userId }: { orgId: string; userId: string }) =>
	performPostRequest<{ permitted: boolean } | null>('/gateway/api/permissions/permitted', {
		body: JSON.stringify({
			resourceId: `ari:cloud:platform::org/${orgId}`,
			principalId: `ari:cloud:identity::user/${userId}`,
			permissionId: 'manage-users',
		}),
	}).then((resp) => !!resp?.permitted);

export type UseIsOrgAdmin = {
	isOrgAdmin: boolean;
	loading: boolean;
	hasErrorOccurred: boolean;
	retry: () => void;
};

export const useIsOrgAdmin = (): UseIsOrgAdmin => {
	const userId = useAccountId();
	const { data: orgId } = useOrgId();

	const checkUserIsOrgAdmin = useCallback(async () => {
		if (!userId || !orgId) {
			return false;
		}
		return getUserOrgPermissions({ orgId, userId });
	}, [orgId, userId]);

	const {
		loading,
		error,
		data,
		fetch: fetchIsOrgAdmin,
	} = useService<boolean>(checkUserIsOrgAdmin, { data: false });

	useEffect(() => {
		fetchIsOrgAdmin();
	}, [fetchIsOrgAdmin]);

	useEffect(() => {
		fireErrorAnalytics({
			meta: {
				id: 'checkUserIsOrgAdmin',
				packageName: 'jiraBusinessGoalPicker',
				teamName: 'jira-cosmos',
			},
			sendToPrivacyUnsafeSplunk: true,
			error,
		});
	}, [error]);

	return {
		isOrgAdmin: !!data,
		loading,
		hasErrorOccurred: !!error,
		retry: fetchIsOrgAdmin,
	};
};
